/**
 * Stage → "current status" band mapping for the tracking map's «По статусу заказа» mode.
 *
 * The four bands mirror the CURRENT-tab stage sub-tabs on the orders page
 * (`STAGE_TAB_DEFS` in features/orders/model/constants) and the badge grouping in
 * `getStageGroupBadge`, so an operator sees the same four colors on both screens.
 *
 * The one deliberate divergence: «Ждет заказ» is grey here (#95A5A6) instead of the
 * orders page's blue, because on a map the courier is *idle at the pickup point* and
 * should read as parked rather than in-transit.
 */

export type TrackingOrderStatusKey =
  | 'to_origin'
  | 'wait_order'
  | 'to_destination'
  | 'wait_receiver';

export interface TrackingOrderStatusDef {
  key: TrackingOrderStatusKey;
  label: string;
  color: string;
}

export const TRACKING_ORDER_STATUS_DEFS: TrackingOrderStatusDef[] = [
  { key: 'to_origin', label: 'Едет к отправителю', color: '#EA580C' },
  { key: 'wait_order', label: 'Ждет заказ', color: '#95A5A6' },
  { key: 'to_destination', label: 'Едет к получателю', color: '#0284C7' },
  { key: 'wait_receiver', label: 'Ждет получателя', color: '#422AFB' },
];

const STATUS_BY_KEY = new Map(TRACKING_ORDER_STATUS_DEFS.map((def) => [def.key, def]));

/**
 * Which of the four bands a raw order `stage` falls into. Ranges are kept in sync with
 * `getStageGroupBadge` (orders): 7 → to_origin, 8 → wait_order, 9-10 → to_destination,
 * 11-14 → wait_receiver. Any other stage (search, returns, terminal) is not an "active
 * delivery" stage and returns null.
 */
export const getOrderStatusKey = (
  stage?: number | null,
): TrackingOrderStatusKey | null => {
  if (stage == null) return null;
  if (stage === 7) return 'to_origin';
  if (stage === 8) return 'wait_order';
  if (stage >= 9 && stage <= 10) return 'to_destination';
  if (stage >= 11 && stage <= 14) return 'wait_receiver';
  return null;
};

/** Band definition (label + color) for a raw order stage, or null when out of band. */
export const getOrderStatusDef = (
  stage?: number | null,
): TrackingOrderStatusDef | null => {
  const key = getOrderStatusKey(stage);
  return key ? (STATUS_BY_KEY.get(key) ?? null) : null;
};

interface ActiveOrderStageLike {
  stage?: number | null | undefined;
}

/**
 * The status band a *courier* is shown as. A courier can hold several active orders
 * (multi-zakaz); we colour by the first order whose stage falls in a band, preserving the
 * order the API returns them in (which is the courier's own delivery sequence).
 */
export const getCourierStatusKey = (
  activeOrders?: ActiveOrderStageLike[] | null,
): TrackingOrderStatusKey | null => {
  for (const order of activeOrders ?? []) {
    const key = getOrderStatusKey(order?.stage);
    if (key) return key;
  }
  return null;
};

/** Courier marker colour in status mode, or null when the courier has no in-band order. */
export const getCourierStatusColor = (
  activeOrders?: ActiveOrderStageLike[] | null,
): string | null => {
  const key = getCourierStatusKey(activeOrders);
  return key ? (STATUS_BY_KEY.get(key)?.color ?? null) : null;
};

/** Band definition (label + color) for a band key, or null when the key is unknown. */
export const getStatusDefByKey = (
  key?: TrackingOrderStatusKey | null,
): TrackingOrderStatusDef | null => (key ? (STATUS_BY_KEY.get(key) ?? null) : null);

/**
 * Total shown on the «По статусу заказа» toggle: the sum of the four band counts from
 * `/orders/status-tab-counts`.
 *
 * Deliberately NOT derived from `/in-radius` `active_orders`, which was the previous
 * source and could not agree with the chips: it counts orders in *any* stage, including
 * the return/search stages (5-6, 16-19) that fall outside the four bands, and it is
 * scoped to the map's radius + equipment filter while the chips are city-wide. Summing
 * the chips makes the toggle total true by construction.
 */
export const getStatusCountsTotal = (
  counts?: Partial<Record<TrackingOrderStatusKey, number>> | null,
): number =>
  TRACKING_ORDER_STATUS_DEFS.reduce((sum, { key }) => sum + (counts?.[key] ?? 0), 0);

/** The «Все» option: show every banded courier, each in its own band's colour. */
export const ALL_STATUSES = 'all' as const;

/**
 * What the map is currently filtered to: one delivery band, or `all` — which draws every
 * courier that falls in ANY of the four bands, each keeping its own colour.
 */
export type TrackingStatusSelection = TrackingOrderStatusKey | typeof ALL_STATUSES;

/** Narrows an arbitrary URL value to a valid selection, falling back to «Все». */
export const parseStatusSelection = (value?: unknown): TrackingStatusSelection => {
  if (value === ALL_STATUSES) return ALL_STATUSES;
  const match = TRACKING_ORDER_STATUS_DEFS.find((def) => def.key === value);
  return match ? match.key : ALL_STATUSES;
};

/**
 * Whether a courier in `key` should be drawn under the current selection.
 * Couriers with no band (`key === null`) are never drawn in status mode — including under
 * «Все», which means "all four bands", not "every courier on the map".
 */
export const isStatusVisible = (
  key: TrackingOrderStatusKey | null,
  selection: TrackingStatusSelection,
): boolean => {
  if (!key) return false;
  return selection === ALL_STATUSES || selection === key;
};

/* -------------------------------------------------------------------------- */
/*  Joining the orders CURRENT tab with /in-radius                            */
/* -------------------------------------------------------------------------- */

/** Minimal slice of an orders CURRENT-tab row we need to band a courier. */
export interface CurrentOrderLike {
  id?: string | number | undefined;
  stage?: number | null | undefined;
  courier?: { id?: string | number | null | undefined } | null | undefined;
}

/**
 * Band key per courier id, built from the orders CURRENT-tab list.
 *
 * Keys are stringified courier ids because the two endpoints disagree on the type:
 * `/in-radius` returns numeric ids while the orders list can return them as strings.
 */
export type CourierStatusIndex = Map<string, TrackingOrderStatusKey>;

/**
 * Indexes CURRENT-tab orders by courier id so the map can colour a courier by the band
 * of the order they are actually carrying, rather than by the `stage` embedded in the
 * `/in-radius` payload (which is a snapshot and can lag the orders list).
 *
 * When a courier holds several active orders (multi-zakaz), the FIRST in-band order wins,
 * matching `getCourierStatusKey`'s multi-order rule so both paths agree.
 */
export const buildCourierStatusIndex = (
  orders?: CurrentOrderLike[] | null,
): CourierStatusIndex => {
  const index: CourierStatusIndex = new Map();

  for (const order of orders ?? []) {
    const courierId = order?.courier?.id;
    if (courierId == null) continue;

    const key = getOrderStatusKey(order?.stage);
    if (!key) continue;

    // First in-band order wins — later orders for the same courier do not override it.
    const idKey = String(courierId);
    if (!index.has(idKey)) index.set(idKey, key);
  }

  return index;
};

/** Courier shape the map resolves a band for. */
interface CourierLike {
  id?: string | number | undefined;
  active_orders?: ActiveOrderStageLike[] | null | undefined;
}

/**
 * The band a courier is drawn as, with the orders CURRENT tab as the authority.
 *
 * Falls back to the `active_orders` embedded in `/in-radius` when the courier has no row
 * in the orders list — which happens legitimately, since the list is paginated and the
 * map is city-wide. Without the fallback those couriers would silently lose their colour.
 */
export const resolveCourierStatusKey = (
  courier: CourierLike | null | undefined,
  index?: CourierStatusIndex | null,
): TrackingOrderStatusKey | null => {
  const id = courier?.id;
  if (id != null) {
    const fromOrders = index?.get(String(id));
    if (fromOrders) return fromOrders;
  }
  return getCourierStatusKey(courier?.active_orders);
};
